/* hs-eslint ignored failing-rules */
/* eslint-disable @typescript-eslint/no-floating-promises */

import I18n from 'I18n';
import { createContext, useEffect, useMemo, useContext, useCallback, useState } from 'react';
import UIButton from 'UIComponents/button/UIButton';
import { RoundelIcon } from '../../../components/Icons/RoundelIcon';
import { theme } from 'academy-common-lib/utils/Styling';
import { useFullPlaylist, useRemoveFromPlaylist, useUpdatePlaylist, useUserOwnsPlaylist } from '../../savedItems';
import { swapArrayItems, useUpdatePlaylistItemPosition } from '..';
import FloatingAlertStore from 'UIComponents/alert/FloatingAlertStore';
import produce from 'immer';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import { jsx as _jsx } from "react/jsx-runtime";
const noop = () => {};
const noopPromise = () => Promise.resolve();
const useLocalDraft = value => {
  const [draft, setDraft] = useState(null);
  useEffect(() => {
    if (value && draft === null) {
      setDraft(value);
    }
  }, [draft, value]);
  return [draft || [], setDraft];
};
const PlaylistItemManagerContext = /*#__PURE__*/createContext({
  playlistId: 0,
  playlistItems: [],
  addPlaylistItem: noop,
  removePlaylistItem: noop,
  movePlaylistItem: noop,
  saveNewPlaylistItems: noopPromise
});
export const usePlaylistItemManager = () => {
  const state = useContext(PlaylistItemManagerContext);
  return Object.assign({}, state, {
    numberOfSelectedItems: state.playlistItems.length
  });
};
export const AddPlaylistItemIcon = ({
  iconSize
}) => {
  const label = I18n.text('learningCenter.savedItems.playlistDetails.addToPlaylist');
  return /*#__PURE__*/_jsx(UITooltip, {
    title: label,
    children: /*#__PURE__*/_jsx(RoundelIcon, {
      backgroundColor: theme.colors.MONOLITH,
      color: theme.colors.OLAF,
      componentProps: {
        'aria-label': label
      },
      name: "add",
      opacity: 0.5,
      size: iconSize === 'sm' ? 10 : 18
    })
  });
};
export const RemovePlaylistItemIcon = ({
  iconSize
}) => {
  const label = I18n.text('learningCenter.savedItems.playlistDetails.removeFromPlaylist');
  return /*#__PURE__*/_jsx(UITooltip, {
    title: label,
    children: /*#__PURE__*/_jsx(RoundelIcon, {
      backgroundColor: theme.colors.CALYPSO_DARK,
      borderColor: undefined,
      color: theme.colors.OLAF,
      componentProps: {
        'aria-label': label
      },
      name: "success",
      size: iconSize === 'sm' ? 10 : 18
    })
  });
};
export const AddRemoveFromPlaylistButtonUnstyled = ({
  playlistItem,
  className,
  iconSize
}) => {
  const {
    removePlaylistItem,
    addPlaylistItem,
    playlistId
  } = usePlaylistItemManager();
  const userOwnsPlaylist = useUserOwnsPlaylist(playlistId);
  const params = {
    playlistId,
    itemId: playlistItem.itemId,
    type: playlistItem.type,
    language: playlistItem.language
  };
  const addToPlaylistHandler = e => {
    e.preventDefault();
    e.stopPropagation();
    addPlaylistItem(params);
  };
  const removeFromPlaylistHandler = e => {
    e.preventDefault();
    e.stopPropagation();
    removePlaylistItem(params);
  };
  if (!userOwnsPlaylist) return null;
  return /*#__PURE__*/_jsx(UIButton, {
    className: className,
    onClick: playlistItem.hasBeenRemoved ? addToPlaylistHandler : removeFromPlaylistHandler,
    use: "unstyled",
    children: playlistItem.hasBeenRemoved ? /*#__PURE__*/_jsx(AddPlaylistItemIcon, {
      iconSize: iconSize
    }) : /*#__PURE__*/_jsx(RemovePlaylistItemIcon, {
      iconSize: iconSize
    })
  });
};
export const PlaylistItemManager = ({
  playlistId,
  render
}) => {
  const {
    playlist
  } = useFullPlaylist(playlistId);
  const [playlistDraft, setPlaylistDraft] = useLocalDraft(playlist && playlist.items);
  const [removeFromPlaylist] = useRemoveFromPlaylist({
    showNotification: false
  });
  const [updatePlaylist] = useUpdatePlaylist(playlistId, {
    onError() {
      setPlaylistDraft(playlist && playlist.items);
      FloatingAlertStore.addAlert({
        type: 'danger',
        message: I18n.text('learningCenter.savedItems.playlistDetails.playlistItemCouldNotBeSaved')
      });
    }
  });
  const updatePlaylistItemPosition = useUpdatePlaylistItemPosition(playlistId, {
    onError() {
      setPlaylistDraft(playlist && playlist.items);
      FloatingAlertStore.addAlert({
        type: 'danger',
        message: I18n.text('learningCenter.savedItems.playlistDetails.playlistItemCouldNotBeMoved')
      });
    }
  });
  const playlistItemsToRender = useMemo(() => {
    if (!playlist || !playlistDraft) return [];
    return playlistDraft.map(playlistItem => Object.assign({}, playlistItem, {
      hasBeenRemoved: !playlist.items.some(item => item.itemId === playlistItem.itemId)
    }));
  }, [playlist, playlistDraft]);
  const addPlaylistItem = useCallback(playlistItem => {
    /**
     * Rather than add the item using the dedicated endpoint, we want
     * to add it preserving the position in the playlist that it had.
     * Otherwise if the user removes an item and then adds it back, it
     * will be at the end of the playlist which is unexpected.
     */
    return updatePlaylist({
      items: playlistItemsToRender.filter(item => {
        if (item.itemId === playlistItem.itemId && item.type === playlistItem.type && item.language === playlistItem.language) {
          return true;
        }
        return !item.hasBeenRemoved;
      })
    });
  }, [playlistItemsToRender, updatePlaylist]);
  const movePlaylistItem = useCallback((fromIndex, toIndex) => {
    updatePlaylistItemPosition(fromIndex, toIndex);
    setPlaylistDraft(oldDraft => produce(oldDraft, newDraft => {
      swapArrayItems(newDraft, fromIndex, toIndex);
    }));
  }, [setPlaylistDraft, updatePlaylistItemPosition]);
  const removePlaylistItem = useCallback(playlistItem => {
    return removeFromPlaylist({
      playlistId,
      itemId: playlistItem.itemId,
      type: playlistItem.type,
      language: playlistItem.language
    });
  }, [playlistId, removeFromPlaylist]);
  const saveNewPlaylistItems = useCallback(items => {
    const newItems = items.filter(item => !item.hasBeenRemoved).map(item => {
      return Object.assign({}, item, {
        type: item.type === 'SHORT_FORM_CONTENT' ? 'SHORT_FORM_CONTENT' : item.type === 'TRACK' ? 'TRACK' : 'LESSON'
      });
    });
    return updatePlaylist({
      items: newItems
    }).then(() => {
      setPlaylistDraft(newItems);
    });
  }, [setPlaylistDraft, updatePlaylist]);
  const contextState = useMemo(() => ({
    playlistId,
    playlistItems: playlistItemsToRender,
    addPlaylistItem,
    removePlaylistItem,
    movePlaylistItem,
    saveNewPlaylistItems
  }), [addPlaylistItem, movePlaylistItem, playlistId, playlistItemsToRender, removePlaylistItem, saveNewPlaylistItems]);
  return /*#__PURE__*/_jsx(PlaylistItemManagerContext.Provider, {
    value: contextState,
    children: render(playlistItemsToRender)
  });
};